import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, forkJoin, of } from 'rxjs';
import { NotificationService, PaginationComponent } from 'src/app/core';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';
import { JobCardService } from 'src/app/modules/job-operation/services/job-card.service';
import { CallOutService } from '../../services';
import { CalloutFindParams, CalloutFindResult, CalloutObject, CalloutPageType, CalloutSave, CalloutStatus, CalloutUpdate, CalloutUserRoles, JobCalloutSummary, OptionItem } from '../../interfaces';

@Component({
  selector: 'app-call-out-view',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  providers: [DatePipe],
  templateUrl: './call-out-view.component.html',
  styleUrls: ['./call-out-view.component.scss']
})
export class CallOutViewComponent implements OnInit, OnChanges {
  @Input() pageType: CalloutPageType = 'cs';
  @Input() userRole: CalloutUserRoles = 'cs';
  @Output() countChanged = new EventEmitter<number>();

  private callOutService = inject(CallOutService);
  private jobCardService = inject(JobCardService);
  private globalDepartmentService = inject(GlobalDepartmentService);
  private notification = inject(NotificationService);

  readonly jobStatuses: OptionItem[] = this.callOutService.calloutStatuses;
  readonly productTypes: OptionItem[] = this.callOutService.productTypes;

  minDate: string = '';

  protected readonly $$totalItems = signal(0);
  protected readonly $$pageNumber = signal(1);
  protected readonly $$itemsPerPage = signal(15);

  // Search parameters matching find API
  callOut_SearchParams: CalloutFindParams = {
    calloutNumber: '',
    jobNumber: '',
    customer: '',
    status: 'pending',
    weekNumber: null,
    cusRequestStartDate: '',
    cusRequestEndDate: '',
    plannerConfirmedStartDate: '',
    plannerConfirmedEndDate: '',
    itemsPerPage: 15,
    pageNumber: 1
  };

  callOutSearchResult: CalloutFindResult[] = [];

  // Modal State
  isCallOffForm = false;
  calloutObject: CalloutObject | undefined = undefined;
  calloutId = -1;
  jobCalloutSummary: JobCalloutSummary | null = null;
  isLoadingSummary = false;

  // Confirmation Modal State
  isConfirmModalOpen = false;
  confirmActionType: 'cancel' | 'reject' | null = null;
  confirmModalTitle = '';
  confirmModalMessage = '';

  // Create Callout Modal State
  isCreateCalloutModalOpen = false;
  isSearchingJobs = false;
  hasSearchedJobs = false;
  jobSearchInput = {
    customer: '',
    pp_number: ''
  };
  jobSearchResults: any[] = [];
  selectedJob: any | null = null;
  newCalloutForm = {
    callOutQty: null as number | null,
    customerRequestedDate: '',
    weekNumber: null as number | null
  };

  ngOnInit(): void {
    const today = new Date();
    this.minDate = today.toISOString().split('T')[0];
    this.searchCallouts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageType'] && !changes['pageType'].firstChange) {
      this.resetForm();
    }
  }

  // ================= Permissions & State Helpers =================
  isCsEditable(): boolean {
    if (!this.calloutObject) return false;
    return this.pageType === 'cs' && (this.calloutObject.status === 'pending' || this.calloutObject.status === 'rejected');
  }

  isPlannerEditable(): boolean {
    if (!this.calloutObject) return false;
    return this.pageType === 'planning' && this.calloutObject.status === 'pending';
  }

  isCsCancelable(): boolean {
    if (!this.calloutObject) return false;
    return this.pageType === 'cs' && this.calloutObject.status === 'pending';
  }

  isPlanningRejectable(): boolean {
    if (!this.calloutObject) return false;
    return this.pageType === 'planning' && this.calloutObject.status === 'pending';
  }

  isPlanningApprovable(): boolean {
    if (!this.calloutObject) return false;
    return this.pageType === 'planning' && this.calloutObject.status === 'pending' && !!this.calloutObject.plannerConfirmedDate;
  }

  public onPageChange(pageNumber: number): void {
    this.callOut_SearchParams.pageNumber = Number(pageNumber);
    this.searchCallouts();
  }

  // ================= Search & Actions =================
  searchCallouts() {
    this.callOutSearchResult = [];

    const params: CalloutFindParams = {
      department: this.globalDepartmentService.getDepartmentId() ?? -1,
      itemsPerPage: this.callOut_SearchParams.itemsPerPage ?? 15,
      pageNumber: this.callOut_SearchParams.pageNumber ?? 1
    };

    if (this.callOut_SearchParams.calloutNumber?.trim()) {
      params.calloutNumber = this.callOut_SearchParams.calloutNumber.trim();
    }
    if (this.callOut_SearchParams.jobNumber?.trim()) {
      params.jobNumber = this.callOut_SearchParams.jobNumber.trim();
    }
    if (this.callOut_SearchParams.customer?.trim()) {
      params.customer = this.callOut_SearchParams.customer.trim();
    }
    if (this.callOut_SearchParams.status) {
      params.status = this.callOut_SearchParams.status;
    }
    if (this.callOut_SearchParams.weekNumber !== null && this.callOut_SearchParams.weekNumber !== undefined && this.callOut_SearchParams.weekNumber !== ('' as any)) {
      params.weekNumber = +this.callOut_SearchParams.weekNumber;
    }
    if (this.callOut_SearchParams.cusRequestStartDate) {
      params.cusRequestStartDate = this.callOut_SearchParams.cusRequestStartDate;
    }
    if (this.callOut_SearchParams.cusRequestEndDate) {
      params.cusRequestEndDate = this.callOut_SearchParams.cusRequestEndDate;
    }
    if (this.callOut_SearchParams.plannerConfirmedStartDate) {
      params.plannerConfirmedStartDate = this.callOut_SearchParams.plannerConfirmedStartDate;
    }
    if (this.callOut_SearchParams.plannerConfirmedEndDate) {
      params.plannerConfirmedEndDate = this.callOut_SearchParams.plannerConfirmedEndDate;
    }

    this.callOutService.findCallout(params).subscribe({
      next: (response) => {
        const data: CalloutFindResult[] = response?.data?.data || response?.data || [];
        this.callOutSearchResult = Array.isArray(data) ? data : [];
        this.$$totalItems.set(response?.data?.totalItems ?? (Array.isArray(data) ? data.length : 0));
        this.$$pageNumber.set(response?.data?.page ?? this.callOut_SearchParams.pageNumber);
        this.$$itemsPerPage.set(response?.data?.itemsPerPage ?? this.callOut_SearchParams.itemsPerPage);
      },
      error: (error) => {
        console.error('Error searching callouts:', error);
        this.notification.set({
          type: 'error',
          message: error?.error?.message || 'Failed to search callouts.'
        });
      }
    });
  }

  resetForm() {
    this.callOut_SearchParams = {
      calloutNumber: '',
      jobNumber: '',
      customer: '',
      status: 'pending',
      weekNumber: null,
      cusRequestStartDate: '',
      cusRequestEndDate: '',
      plannerConfirmedStartDate: '',
      plannerConfirmedEndDate: '',
      itemsPerPage: 15,
      pageNumber: 1
    };
    this.$$pageNumber.set(1);
    this.searchCallouts();
  }

  prepareDataForCallout(calloutItem: CalloutFindResult) {
    this.calloutId = +calloutItem.id;
    this.isCallOffForm = true;

    const jobNumber = calloutItem.jobNumber || '';

    this.calloutObject = {
      id: +calloutItem.id,
      calloutNumber: calloutItem.calloutNumber || `CO-${calloutItem.id}`,
      jobNumber: jobNumber,
      productType: calloutItem.productType || '',
      customer: calloutItem.customer || '',
      callOutQuantity: +(calloutItem.callOutQty || 0),
      deliveredQuantity: +(calloutItem.deliveredQty || 0),
      customerRequestedDate: calloutItem.cusRequestDate || '',
      plannerConfirmedDate: calloutItem.plannerConfirmedDate || '',
      lastDispatchNumber: calloutItem.lastDispatchNumber || '',
      lastDispatchId: +(calloutItem.lastDispatchId || -1),
      LastDispatchDate: calloutItem.lastDispatchDate || '',
      status: calloutItem.status,
      deliveredDate: calloutItem.deliveredDate || '',
      weekNumber: +(calloutItem.weekNumber || 0),
      dispatchNumbers: Array.isArray(calloutItem.dispatchNumbers) ? calloutItem.dispatchNumbers.map((dispatch: any) => JSON.parse(dispatch)) : []
    };

    this.fetchJobAndCalloutSummary(jobNumber);
  }

  fetchJobAndCalloutSummary(jobNumber: string) {
    if (!jobNumber) return;
    this.isLoadingSummary = true;
    this.jobCalloutSummary = null;

    const jobParams = {
      customer: '',
      pp_number: jobNumber,
      status: '',
      delivery_date: '',
      machine_id: -1,
      department_id: this.globalDepartmentService.getDepartmentId() ?? -1,
      page_number: 1,
      items_per_page: 15,
      request_date: '',
      cs_released_at: ''
    };

    const jobReq = this.jobCardService.find(jobParams).pipe(catchError(() => of(null)));
    const calloutsReq = this.callOutService.findCallout({ jobNumber: jobNumber, itemsPerPage: 1000, pageNumber: 1, status: '' }).pipe(catchError(() => of(null)));

    forkJoin([jobReq, calloutsReq]).subscribe({
      next: ([jobRes, calloutsRes]: [any, any]) => {
        this.isLoadingSummary = false;

        // 1. Get Job quantity from JobCard service
        const jobData = jobRes?.data?.data || [];
        const matchingJob = Array.isArray(jobData)
          ? jobData.find((j: any) => j.ppNo?.trim().toLowerCase() === jobNumber.trim().toLowerCase()) || jobData[0]
          : null;

        const jobQty = matchingJob ? +(matchingJob.quantity || 0) : 0;

        // 2. Get Callouts breakdown by status (pending, approved, rejected, cancelled)
        const calloutData: CalloutFindResult[] = calloutsRes?.data?.data || [];
        const calloutsList = Array.isArray(calloutData) ? calloutData : [];

        let pendingQty = 0;
        let approvedQty = 0;
        let rejectedQty = 0;
        let cancelledQty = 0;

        for (const item of calloutsList) {
          const qty = +(item.callOutQty || 0);
          const status = (item.status || '').toLowerCase();
          if (status === 'pending') {
            pendingQty += qty;
          } else if (status === 'approved') {
            approvedQty += qty;
          } else if (status === 'rejected') {
            rejectedQty += qty;
          } else if (status === 'cancelled') {
            cancelledQty += qty;
          }
        }

        this.jobCalloutSummary = {
          jobNumber: jobNumber,
          jobQuantity: jobQty,
          pendingQuantity: pendingQty,
          approvedQuantity: approvedQty,
          rejectedQuantity: rejectedQty,
          cancelledQuantity: cancelledQty
        };
      },
      error: (err) => {
        this.isLoadingSummary = false;
        console.error('Error fetching job and callout summary:', err);
      }
    });
  }

  closeCallOffForm() {
    this.isCallOffForm = false;
    this.calloutObject = undefined;
    this.calloutId = -1;
    this.jobCalloutSummary = null;
    this.isLoadingSummary = false;
  }

  // ================= Create Call Out Flow =================
  openCreateModal() {
    this.isCreateCalloutModalOpen = true;
    this.jobSearchInput = { customer: '', pp_number: '' };
    this.jobSearchResults = [];
    this.hasSearchedJobs = false;
    this.selectedJob = null;
    this.jobCalloutSummary = null;
    this.isLoadingSummary = false;
    this.newCalloutForm = {
      callOutQty: null,
      customerRequestedDate: '',
      weekNumber: null
    };
  }

  closeCreateModal() {
    this.isCreateCalloutModalOpen = false;
    this.selectedJob = null;
    this.jobSearchResults = [];
    this.hasSearchedJobs = false;
    this.jobCalloutSummary = null;
    this.isLoadingSummary = false;
  }

  searchJobs() {
    this.isSearchingJobs = true;
    this.hasSearchedJobs = true;
    this.jobSearchResults = [];

    const searchParams = {
      customer: this.jobSearchInput.customer,
      pp_number: this.jobSearchInput.pp_number,
      status: '',
      delivery_date: '',
      machine_id: -1,
      department_id: this.globalDepartmentService.getDepartmentId() ?? -1,
      page_number: 1,
      items_per_page: 15,
      request_date: '',
      cs_released_at: ''
    };

    this.jobCardService.find(searchParams).subscribe({
      next: (res: any) => {
        this.isSearchingJobs = false;
        const data = res?.data?.data || [];
        this.jobSearchResults = Array.isArray(data) ? data : [];
      },
      error: (err: any) => {
        this.isSearchingJobs = false;
        console.error('Error finding jobs:', err);
        this.notification.set({
          type: 'error',
          message: err?.error?.message || 'Error searching jobs.'
        });
      }
    });
  }

  selectJob(job: any) {
    this.selectedJob = job;
    this.newCalloutForm.callOutQty = job.quantity || null;
    this.newCalloutForm.customerRequestedDate = job.requestDate || '';
    if (job?.ppNo) {
      this.fetchJobAndCalloutSummary(job.ppNo);
    }
  }

  resetSelectedJob() {
    this.selectedJob = null;
    this.jobCalloutSummary = null;
    this.isLoadingSummary = false;
  }

  submitCreateCallout() {
    if (!this.selectedJob) {
      this.notification.set({ type: 'alert', message: 'Please select a job first.' });
      return;
    }

    if (!this.newCalloutForm.callOutQty || this.newCalloutForm.callOutQty < 1) {
      this.notification.set({ type: 'alert', message: 'Please enter a valid call out quantity.' });
      return;
    }

    if (!this.newCalloutForm.customerRequestedDate) {
      this.notification.set({ type: 'alert', message: 'Customer requested date is required.' });
      return;
    }

    if (!this.newCalloutForm.weekNumber || this.newCalloutForm.weekNumber <= 0) {
      this.notification.set({ type: 'alert', message: 'Week number is required.' });
      return;
    }

    const payload: CalloutSave = {
      jobNumber: this.selectedJob.ppNo,
      customer: this.selectedJob.customer || '',
      callOutQty: +this.newCalloutForm.callOutQty,
      cusRequestDate: this.newCalloutForm.customerRequestedDate,
      weekNumber: +(this.newCalloutForm.weekNumber || 0),
      productType: this.selectedJob.productType || '',
    };

    this.callOutService.createCallout(payload).subscribe({
      next: () => {
        this.notification.set({
          type: 'success',
          message: 'Call Out created successfully with pending status!'
        });
        this.closeCreateModal();
        this.searchCallouts();
      },
      error: (error: any) => {
        console.error('Error creating callout:', error);
        this.notification.set({
          type: 'error',
          message: error?.error?.message || 'Failed to create call out.'
        });
      }
    });
  }

  updateCallOffData() {
    if (!this.calloutObject) {
      this.notification.set({ type: 'error', message: 'Unknown error occurred.' });
      return;
    }

    if (!this.calloutObject.callOutQuantity || this.calloutObject.callOutQuantity < 1) {
      this.notification.set({ type: 'alert', message: 'Please enter a valid call out quantity.' });
      return;
    }

    if (!this.calloutObject.customerRequestedDate) {
      this.notification.set({ type: 'alert', message: 'Customer requested date is required.' });
      return;
    }

    if (this.calloutObject.customerRequestedDate < this.minDate) {
      this.notification.set({ type: 'alert', message: 'Customer requested date should be greater than or equal to current date.' });
      return;
    }

    if (!this.calloutObject.weekNumber || this.calloutObject.weekNumber <= 0) {
      this.notification.set({ type: 'alert', message: 'Week number is required.' });
      return;
    }


    const calloutSave: CalloutUpdate = {
      callOutQty: +this.calloutObject.callOutQuantity,
      cusRequestDate: this.calloutObject.customerRequestedDate,
      plannerConfirmedDate: this.calloutObject.plannerConfirmedDate || '',
      weekNumber: +(this.calloutObject.weekNumber || 0),
    };

    const isRejected = this.calloutObject.status === 'rejected';

    this.callOutService.updateCallout(calloutSave, this.calloutId.toString()).subscribe({
      next: () => {
        if (isRejected) {
          this.callOutService.updateStatus(this.calloutId.toString(), 'pending').subscribe({
            next: () => {
              this.notification.set({
                type: 'success',
                message: 'Callout updated and status reset to pending successfully!'
              });
              this.closeCallOffForm();
              this.searchCallouts();
            },
            error: (statusErr) => {
              console.error('Error resetting callout status to pending:', statusErr);
              this.notification.set({
                type: 'error',
                message: statusErr?.error?.message || 'Callout updated, but failed to reset status to pending.'
              });
            }
          });
        } else {
          this.notification.set({ type: 'success', message: 'Callout updated successfully!' });
          this.closeCallOffForm();
          this.searchCallouts();
        }
      },
      error: (error) => {
        console.error('Error saving callout data:', error);
        this.notification.set({ type: 'error', message: error?.error?.message || 'Error saving callout data.' });
      }
    });
  }

  approveCallout() {
    if (!this.calloutObject) return;

    if (!this.calloutObject.plannerConfirmedDate || this.calloutObject.plannerConfirmedDate === '') {
      this.notification.set({ type: 'alert', message: 'Planned confirmed date is required to approve callout.' });
      return;
    }

    // Step 1: Update callout data (planned confirmed date) via Callout Patch request
    const patchPayload: CalloutUpdate = {
      plannerConfirmedDate: this.calloutObject.plannerConfirmedDate,
      callOutQty: +this.calloutObject.callOutQuantity,
      cusRequestDate: this.calloutObject.customerRequestedDate,
      weekNumber: this.calloutObject.weekNumber
    };

    this.callOutService.updateCallout(patchPayload, this.calloutId.toString()).subscribe({
      next: () => {
        // Step 2: Update status to 'approved' via separate Status Patch request
        this.callOutService.updateStatus(this.calloutId.toString(), 'approved').subscribe({
          next: () => {
            this.notification.set({
              type: 'success',
              message: 'Callout approved successfully with planned confirmed date!'
            });
            this.closeCallOffForm();
            this.searchCallouts();
          },
          error: (statusErr) => {
            console.error('Error updating callout status:', statusErr);
            this.notification.set({
              type: 'error',
              message: statusErr?.error?.message || 'Planned date updated, but failed to update status.'
            });
          }
        });
      },
      error: (patchErr) => {
        console.error('Error updating callout details:', patchErr);
        this.notification.set({
          type: 'error',
          message: patchErr?.error?.message || 'Failed to update planned confirmed date.'
        });
      }
    });
  }

  promptRejectCallout() {
    if (!this.calloutObject) return;
    this.confirmActionType = 'reject';
    this.confirmModalTitle = 'Reject Callout';
    this.confirmModalMessage = 'Are you sure you want to reject this callout?';
    this.isConfirmModalOpen = true;
  }

  promptCancelCallout() {
    if (!this.calloutObject) return;
    this.confirmActionType = 'cancel';
    this.confirmModalTitle = 'Cancel Callout';
    this.confirmModalMessage = 'Are you sure you want to cancel this callout?';
    this.isConfirmModalOpen = true;
  }

  onConfirmAction() {
    this.isConfirmModalOpen = false;
    if (this.confirmActionType === 'reject') {
      this.updateCalloutStatus('rejected');
    } else if (this.confirmActionType === 'cancel') {
      this.updateCalloutStatus('cancelled');
    }
    this.confirmActionType = null;
  }

  onCancelConfirmAction() {
    this.isConfirmModalOpen = false;
    this.confirmActionType = null;
  }

  updateCalloutStatus(status: CalloutStatus) {
    if (!this.calloutObject) return;

    this.callOutService.updateStatus(this.calloutId.toString(), status).subscribe({
      next: () => {
        this.notification.set({
          type: 'success',
          message: `Callout status updated to ${status} successfully!`
        });
        this.closeCallOffForm();
        this.searchCallouts();
      },
      error: (error) => {
        console.error('Error updating callout status:', error);
        this.notification.set({
          type: 'error',
          message: error?.error?.message || `Error updating callout status to ${status}.`
        });
      }
    });
  }

  selectAll(event: MouseEvent): void {
    (event.target as HTMLInputElement).select();
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'rejected':
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'delivered':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  }
}
